









































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ApexOptions } from 'apexcharts';

import CheckboxLegend from '@/componentsV2/molecules/Chart/CheckboxLegend.vue';
import RadioLegend from '@/componentsV2/molecules/Chart/RadioLegend.vue';

import { IChartSerie } from '@/models/performance';

import isMobile from '@/utils/mobile-check';

import segmentIo from '@/utils/segment.io';

type LegendGATracking = {
  eventCategory: string;
  eventAction: string;
};

@Component({
  components: {
    CheckboxLegend,
    RadioLegend
  }
})
export default class BaseChart extends Vue {
  @Prop(String) instanceId!: string;
  @Prop(String) type!: string;
  @Prop({ default: '100%' }) width?: number;
  @Prop({ default: isMobile() ? 200 : 260 }) height?: number;
  @Prop({ default: () => ({}) }) options!: ApexOptions;
  @Prop({ default: () => [] }) series!: IChartSerie[];
  @Prop({ default: false }) loading?: boolean;
  @Prop({ default: true }) showLegend?: boolean;
  @Prop({ default: false }) alwaysShowLegend?: boolean;
  @Prop({ default: true }) checkboxLegend?: boolean;
  @Prop({ default: false }) showBulletOneData?: boolean;
  @Prop() legendGATracking?: LegendGATracking;
  @Prop() legendSegmentTrackingName?: string;

  legendState: Record<string, { state: boolean; color: string }> = {};

  get enableLegend() {
    return this.alwaysShowLegend
      ? Object.keys(this.legendState).length > 0
      : Object.keys(this.legendState).length > 1 && this.series.length;
  }

  get hasOptions() {
    return Object.keys(this.options).length;
  }

  get activeSeries() {
    return this.series[this.legendActiveIndex] || {};
  }

  get legendActiveIndex() {
    return Object.keys(this.legendState).findIndex(
      serie => !!this.legendState[serie].state
    );
  }

  get showBullet() {
    return (
      this.showBulletOneData &&
      this.series.length > 0 &&
      this.series[0].data &&
      this.series[0].data.length === 1
    );
  }

  get chartHasData() {
    return this.series.some(({ data: [, value] }) => value);
  }

  @Watch('series', { immediate: true, deep: true })
  updateLegendState() {
    if (!this.series.length || !this.hasOptions) return;

    const chartEl = this.$refs[this.instanceId] as any;

    let colors = this.options.colors || [];

    if (!colors.length && chartEl) {
      colors = chartEl.chart.w.globals.colors;
    }
    const legendState = this.series
      .map(serie => serie.name)
      .reduce(
        (acc, curr, index) => ({
          ...acc,
          [curr]: {
            state: this.legendState[curr]
              ? this.legendState[curr].state
              : !!this.checkboxLegend,
            color: colors[index]
          }
        }),
        {}
      );

    this.legendState = legendState;
    if (!this.checkboxLegend) {
      if (this.verifySeriesLoaded()) {
        setTimeout(() => {
          this.selectOneSeries(
            Object.keys(legendState).filter(
              serie => !!this.legendState[serie].state
            )[0] || Object.keys(legendState)[0]
          );
        });
      }
    }
  }

  selectOneSeries(serie: string) {
    const chartEl = this.$refs[this.instanceId] as any;

    if (!chartEl) return;

    this.resetChart();
    if (this.legendState[serie]) {
      this.legendState[serie].state = true;
      chartEl.toggleSeries(serie);
    }
  }

  resetChart() {
    const chartEl = this.$refs[this.instanceId] as any;
    if (!chartEl) return;

    for (let i = 0; i < Object.keys(this.legendState).length; i++) {
      const element = Object.keys(this.legendState)[i];
      if (this.legendState[element]) this.legendState[element].state = false;
      chartEl.hideSeries(element);
    }
  }

  toggleSeries(legendVisibility: boolean, legendLabel: string) {
    if (!this.chartHasData && this.checkboxLegend) return;
    if (this.legendSegmentTrackingName)
      segmentIo.track(this.legendSegmentTrackingName, {
        Field: 'legend',
        Value: legendLabel
      });

    const chartEl = this.$refs[this.instanceId] as any;

    if (!chartEl) return;

    if (!this.checkboxLegend) {
      this.resetChart();
      if (this.legendState[legendLabel])
        this.legendState[legendLabel].state = true;
    }
    chartEl.toggleSeries(legendLabel);
  }

  verifySeriesLoaded() {
    return this.series.every(serie => serie.loaded !== false);
  }

  mounted() {
    if (!this.checkboxLegend && Object.keys(this.legendState).length > 0) {
      const allSeriesLoaded = this.verifySeriesLoaded();
      if (allSeriesLoaded) {
        const firstSerie = Object.keys(this.legendState)[0];
        this.selectOneSeries(firstSerie);
      }
    }
  }
}
